import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { bindActionCreators } from "redux";

import { NavLink } from "react-router-dom";

import { BackIcon } from "../../../assets";
import InfoIconSvg from "../../../assets/svgs/infoIcon.svg";

import {
  Button,
  Record,
  Tooltip,
} from "../../../components";
import {
  getRide,
  clearCurrentRide,
  setShowRefreshNotification,
} from "../../../store/rides";
import { fetchHOPreferences } from "../../../store/health_organizations";
import { getRideRequestOptions } from "../../../store/selectors/healthOrganizations";
import { DateHelper, LodashHelper, renderRecordValue, baseRequest } from "../../../utils";
import {
  ADDITIONAL_DELAY_FOR_RIDE_CREATION,
  DEFAULT_TIME_FORMAT,
  URLS,
  UBER_TEXT,
} from "../../../constants/constants";

import { usePreventLeave } from "../../../hooks/usePreventLeave";
import {
  getOtherLegSeqIdOfRoundtrip,
  renderPhoneNumber,
} from "../../../utils/helpers";
import {
  getAdditionalRideOptions,
  getPlatformSettings,
} from "../../../store/preferences";

import { destroyNotification } from "../../../components/common/ConfirmPopUp/ConfirmPopUp";
import { renderEditLeaveModal } from "../EditDraft/renderEditLeaveModal";

import styles from "./index.module.css";
import { Navigation } from "../../../utils/navigation";
import { RescheduleRideForm } from "../../../components/forms/CreateRide/StepThree/RescheduleRideForm";
import { ConfirmRescheduleRideModal } from "../../../components/modals/ConfirmRescheduleRideModal/ConfirmRescheduleRideModal";
import { defaultTimeValues } from "../../../components/forms/Preferences/MHPreferences/partials/defaultValues";
import { UberHealthTag } from "../../../components/common/UberHealthTag/UberHealthTag";
import { vehicleOptionsDictionary } from "../../../dictionaries";

const MIN_DELAY_UBER = { hours: 0, minutes: 35 };

const UberRescheduleRide = ({
  getRide,
  ride,
  platformPreferences,
  fetchHOPreferences,
  getAdditionalRideOptions,
  getPlatformSettings,
  setShowRefreshNotification,
  clearCurrentRide,
  role,
}) => {
  const [editSeriesOption, setEditSeriesOption] = useState(null);
  const navigate = Navigation();
  const [openModal, setOpenModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { id } = useParams();

  const draft = useMemo(() => {
    const roundtripRide = {
      rescheduledDate: ride?.pickUpDate,
      isRoundTrip: ride?.isRoundTrip,
      connectedLegId: ride?.connectedLegId,
      connectedLegRescheduleDate: ride?.connectedLeg?.pickUpDate,
    };

    return roundtripRide;
  }, [ride]);

  useEffect(
    () => () => {
      clearCurrentRide();
      setShowRefreshNotification(false);
      destroyNotification();
    },
    [clearCurrentRide, setShowRefreshNotification],
  );

  const handleSaveChanges = () => {
    setOpenModal(true);
  };

  const [formikValues, setFormikValues] = useState(draft);
  const [formIsValid, setFormikIsValid] = useState(null);

  const [visibleModal, setModalVisible, unblockHistory, location] =
    usePreventLeave(draft, formikValues);

  const handleStay = () => {
    setModalVisible(false);
  };

  const handleLeave = () => {
    if (unblockHistory) {
      unblockHistory.current();
      navigate.push(location.location);
    }
  };

  const onConfirm = async (reason) => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      if (unblockHistory) {
        unblockHistory.current();
      }

      await baseRequest("POST", `/uber-health/rides/${id}/resubmit`, {
        reason,
        newPickupDate: formikValues?.rescheduledDate,
      });

      setOpenModal(false);
      navigate.push(URLS.RIDES);
    } catch (e) {
      // Optionally handle error (toast, etc.)
    } finally {
      setIsSubmitting(false);
    }
  };


  useEffect(() => {
    getAdditionalRideOptions();
    return;
  }, [getAdditionalRideOptions]);

  useEffect(() => {
    getRide(id, navigate);
    return;
  }, [getRide, id]);

  useEffect(() => {
    getPlatformSettings();
    return;
  }, [getPlatformSettings]);

  useEffect(() => {
    const query = new URLSearchParams(window?.location?.search);
    setEditSeriesOption(query.get("mode"));
    return;
  }, [id]);

  const handleBack = () => navigate.push(URLS.RIDES);

  const saveChangesButtonIsDisabled = useMemo(() => {
    return !formIsValid;
  }, [draft, formikValues, formIsValid]);

  const syncFormikValues = useCallback(
    (values, isValid) => {
      const updatedValues = {
        ...values,
        connectedLegRescheduleDate: values.connectedLegRescheduleDate || "",
      };

      setFormikValues((prevFormikValues) => {
        if (!LodashHelper.isEqual(prevFormikValues, updatedValues)) {
          return updatedValues;
        }
        return prevFormikValues;
      });

      setFormikIsValid(isValid);
    },
    [],
  );

  return (
    <div className={styles.pageContent}>
      <div className={styles.header}>
        <div className={styles.backBtn} onClick={handleBack}>
          <BackIcon width="24px" height="24px" fill="#e1f4d3
" />
        </div>
        <h1 className={styles.pageTitle}>
          {UBER_TEXT.RESCHEDULE_MODAL_TITLE_PREFIX_UBER} {ride?.seqId}
        </h1>
      </div>
      <div className={styles.editRide}>
        <Record
          title="Rider Name"
          value={`${renderRecordValue(ride?.riderInfo?.firstName)} ${renderRecordValue(
            ride?.riderInfo?.lastName,
          )}`}
        />
        <Record
          title={UBER_TEXT.UBER_REREQUEST_ORIGINAL_SUBMITTED_TITLE}
          value={renderRecordValue(
            ride?.createdAt &&
            DateHelper.format(new Date(ride?.createdAt), DEFAULT_TIME_FORMAT),
          )}
          myClassName={styles.createdAt}
        />
        <Record
          title="Created By"
          value={`${renderRecordValue(
            ride?.createdBy?.firstName,
          )} ${renderRecordValue(ride?.createdBy?.lastName)}`}
        />
        {ride?.vehicleType === "uberAmbulatoryRide" && (
          <Record
            title="Vehicle Type"
            value={
              <UberHealthTag label={vehicleOptionsDictionary.uberAmbulatoryRide} />
            }
            myClassName={styles.vehicleType}
          />
        )}
        {ride && (
          <div className={styles.editForm}>
            <div className={styles.formBlock}>
              <h2>Ride Information</h2>
            </div>
            <div className={styles.formBlock}>
              <RescheduleRideForm
                minDelayToStartRide={MIN_DELAY_UBER}
                vehicleType={ride?.vehicleType}
                setFormikValues={syncFormikValues}
                values={ride}
              />
            </div>
            <div className={styles.formBlock} />
            {openModal && (
              <ConfirmRescheduleRideModal
                ride={ride}
                handleConfirm={(reason) => onConfirm(reason)}
                handleCancel={() => !isSubmitting && setOpenModal(false)}
                isSubmitting={isSubmitting}
                isUberReRequest
              />
            )}
            <p className={styles.sendDescription}>
              After pressing "Confirm" button, this request will be sent to
              our network of transportation providers
            </p>
            <div className={styles.btns}>
              <div className={styles.buttonControl}>
                <Button myClassName={styles.navigationBtn} onClick={handleBack}>
                  Cancel
                </Button>
              </div>
              <div className={styles.buttonControl}>
                <Button
                  disabled={saveChangesButtonIsDisabled}
                  onClick={handleSaveChanges}
                  type="button"
                >
                  Confirm
                </Button>

                <div className={styles.toolTip}>
                  <Tooltip
                    title={
                      <span className={styles.tooltipContent}>
                        {UBER_TEXT.REREQUEST_PAGE_TOOLTIP}
                      </span>
                    }
                  >
                    <span className={styles.tooltipInfo}>
                      <img src={InfoIconSvg} alt="info" className={styles.infoIcon} />
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {renderEditLeaveModal({
        visibleModal,
        handleStay,
        handleLeave,
      })}
    </div>
  );
};

const mapStateToProps = (state) => ({
  ride: state.rides.ride,
  role: state.authorization.role,
  rideRequestFields: getRideRequestOptions(state),
  hasMatchedTC: state.rides.hasMatchedTC,
  rideOptions: state.preferences.rideOptions?.filter(
    (option) => !option.disabled,
  ),
  platformPreferences: state.preferences.platformPreferences,
  airportBufferTime: state?.healthOrganizations?.HOPreferences?.airportBufferTime,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getRide,
      fetchHOPreferences,
      getAdditionalRideOptions,
      getPlatformSettings,
      setShowRefreshNotification,
      clearCurrentRide,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(UberRescheduleRide);

